/** The shop's journal: who did what, when, on which device. */
const journal = {
  en: {
    title: "Journal",
    subtitle: "Everything that happened in the shop — who did it, when, and on which device. Nothing here can be changed.",
    loadFailed: "Could not load the journal.",
    empty: "Nothing recorded in this period.",
    loadMore: "Show older entries",
    person: "Person",
    everyone: "Everyone",
    type: "Type",
    everything: "Everything",
    unknownPerson: "Nobody signed in",
    categories: {
      sales: "Sales",
      stock: "Stock",
      catalogue: "Products & prices",
      money: "Money",
      preorders: "Pre-orders",
      till: "Till",
      team: "Team & settings",
    },
    actions: {
      sale: {
        created: "Sale #{receipt} — {total}, {payment}",
        refunded: "Refunded sale #{receipt} — {total}",
      },
      stock: {
        adjusted: "{product}: stock {from} → {to} ({delta})",
        moved_to_warehouse: "Moved {quantity} × {product} to the warehouse",
        moved_to_shop: "Moved {quantity} × {product} to the shop",
        warehouse_adjusted: "{product}: warehouse stock {from} → {to} ({delta})",
      },
      product: {
        created: "Added {product}",
        updated: "Edited {product}",
        archived: "Archived {product}",
        deleted: "Deleted {product}",
        restored: "Restored {product}",
        converted_to_range: "Turned {product} into the range “{range_name}” ({variants} variants)",
      },
      offer: {
        created: "Created the offer “{offer_title}”",
        updated: "Edited the offer “{offer_title}”",
        deleted: "Deleted the offer “{offer_title}”",
      },
      pack: {
        created: "Created the pack “{pack_name}”",
        updated: "Edited the pack “{pack_name}”",
        deleted: "Deleted the pack “{pack_name}”",
      },
      credit: {
        payment: "{credit_tab} paid {amount} towards their tab",
      },
      expense: {
        created: "Expense: {label} — {amount}",
        updated: "Edited the expense “{label}”",
        deleted: "Removed the expense “{label}” ({amount})",
      },
      preorder: {
        status: "Pre-order for {customer}: {from} → {to}",
      },
      till: {
        unlocked: "Opened the till",
        pin_failed: "A PIN was typed that matches nobody",
        device_activated: "Activated the device “{device_label}”",
        device_revoked: "Released the device “{device_label}”",
      },
      staff: {
        added: "Added {name} to the team",
        updated: "Changed what {name} may do",
        removed: "Removed {name} from the team",
        password_reset: "Reset {name}'s password",
        pin_changed: "Changed their till PIN",
      },
      catalog: {
        copied: "Added {added} products from the catalogue ({drafts} to price)",
        copied_from_shop: {
          one: "Copied {count} product from {from_shop}",
          other: "Copied {count} products from {from_shop}",
        },
      },
      doses: {
        bottle_opened: "Opened a bottle of {product} to sell by the dose",
        bottle_finished: "Finished a bottle of {product}: {doses_sold} doses",
        bottle_cancelled: "Put an unopened bottle of {product} back on the shelf",
        setup_changed: { one: "Changed selling by the dose for {count} liquid", other: "Changed selling by the dose for {count} liquids" },
      },
      shop: {
        updated: "Edited the shop's details",
        link_changed: "Changed the shop's link",
      },
      settings: {
        changed: "Changed the setting “{key}”: {from} → {to}",
      },
      cash: {
        opened: "Opened the drawer, counted {counted}",
        closed: "Closed the drawer, counted {counted}",
        cash_in: "Put {amount} of change in the drawer",
        withdrawal: "Took {amount} out of the drawer",
        refund: "Paid a refund of {amount} out of the drawer",
        tab_payment: "{amount} paid back on a tab, into the drawer",
        supplier_payout: "Paid a supplier {amount} out of the drawer",
      },
      savings: {
        deposit: "Added {amount} to the savings",
        withdraw: "Took {amount} out of the savings",
      },
      supplier: {
        created: "Added the supplier {supplier_name}",
        payment: "Paid {supplier_name} {amount} ({source})",
      },
      delivery: {
        recorded: "Delivery from {supplier_name} — {total}",
        priced: "Priced the delivery from {supplier_name} — {total}",
      },
      mixing: {
        batch_produced: "Mixed {bottles} × {bottle_size_ml} ml of {recipe_name}",
        batch_shelved: "Put {bottles} bottles of {recipe_name} on the shelf",
        batch_discarded: "Threw away a batch of {recipe_name}",
        material_adjusted: "{material_name}: {delta} {unit}",
      },
    },
    lines: {
      drawerCount: "Should have held {expected} · difference {difference}",
      drawerSplit: "{taken} taken out, {left} left for the next day",
      awaitingPrices: "Some prices still to fill in",
      onTab: "On {name}'s tab",
      customer: "Customer: {name}",
      discount: "Discount: {amount}",
      approvedBelowCost: "Below cost — approved by {name}",
      belowCost: "Below cost (an offer or a pack)",
      reason: "Why: {reason}",
    },
    fields: {
      name: "Name",
      price: "Price",
      cost_price: "Buy price",
      discount_percentage: "Discount %",
      stock_quantity: "Stock",
      category: "Category",
      is_draft: "Draft",
      is_active: "Active",
      title: "Title",
      discount_value: "Value",
      start_date: "From",
      end_date: "To",
      items: "Contents",
      products: "Products covered",
      ranges: "Ranges covered",
      role: "Role",
      permissions: "Permissions",
      pin: "Till PIN",
      label: "Label",
      amount: "Amount",
      occurred_on: "Date",
      slug: "Link",
      menu_show_prices: "Menu shows prices",
      preorders_enabled: "Takes pre-orders",
      warehouse_enabled: "Uses a warehouse",
      opening_hours: "Opening hours",
    },
    sources: { drawer: "from the drawer", safe: "from the savings", outside: "not from the drawer" },
    values: { yes: "yes", no: "no", changed: "changed" },
  },

  fr: {
    title: "Journal",
    subtitle: "Tout ce qui s'est passé dans la boutique — qui, quand et sur quel appareil. Rien ici ne peut être modifié.",
    loadFailed: "Impossible de charger le journal.",
    empty: "Rien d'enregistré sur cette période.",
    loadMore: "Voir les entrées plus anciennes",
    person: "Personne",
    everyone: "Tout le monde",
    type: "Type",
    everything: "Tout",
    unknownPerson: "Personne de connecté",
    categories: {
      sales: "Ventes",
      stock: "Stock",
      catalogue: "Produits et prix",
      money: "Argent",
      preorders: "Précommandes",
      till: "Caisse",
      team: "Équipe et réglages",
    },
    actions: {
      sale: {
        created: "Vente n°{receipt} — {total}, {payment}",
        refunded: "Vente n°{receipt} remboursée — {total}",
      },
      stock: {
        adjusted: "{product} : stock {from} → {to} ({delta})",
        moved_to_warehouse: "{quantity} × {product} déplacé(s) à l'entrepôt",
        moved_to_shop: "{quantity} × {product} déplacé(s) en boutique",
        warehouse_adjusted: "{product} : stock entrepôt {from} → {to} ({delta})",
      },
      product: {
        created: "{product} ajouté",
        updated: "{product} modifié",
        archived: "{product} archivé",
        deleted: "{product} supprimé",
        restored: "{product} restauré",
        converted_to_range: "{product} transformé en la gamme « {range_name} » ({variants} variantes)",
      },
      offer: {
        created: "Offre « {offer_title} » créée",
        updated: "Offre « {offer_title} » modifiée",
        deleted: "Offre « {offer_title} » supprimée",
      },
      pack: {
        created: "Pack « {pack_name} » créé",
        updated: "Pack « {pack_name} » modifié",
        deleted: "Pack « {pack_name} » supprimé",
      },
      credit: {
        payment: "{credit_tab} a réglé {amount} sur son compte",
      },
      expense: {
        created: "Dépense : {label} — {amount}",
        updated: "Dépense « {label} » modifiée",
        deleted: "Dépense « {label} » supprimée ({amount})",
      },
      preorder: {
        status: "Précommande de {customer} : {from} → {to}",
      },
      till: {
        unlocked: "A ouvert la caisse",
        pin_failed: "Un code PIN inconnu a été saisi",
        device_activated: "Appareil « {device_label} » activé",
        device_revoked: "Appareil « {device_label} » libéré",
      },
      staff: {
        added: "{name} ajouté à l'équipe",
        updated: "Droits de {name} modifiés",
        removed: "{name} retiré de l'équipe",
        password_reset: "Mot de passe de {name} réinitialisé",
        pin_changed: "Code PIN de caisse modifié",
      },
      catalog: {
        copied: "{added} produits ajoutés depuis le catalogue ({drafts} à tarifer)",
        copied_from_shop: {
          one: "{count} produit copié depuis {from_shop}",
          other: "{count} produits copiés depuis {from_shop}",
        },
      },
      doses: {
        bottle_opened: "Bouteille de {product} ouverte pour la vente à la dose",
        bottle_finished: "Bouteille de {product} terminée : {doses_sold} doses",
        bottle_cancelled: "Bouteille de {product} ouverte par erreur, remise en stock",
        setup_changed: { one: "Vente à la dose modifiée pour {count} liquide", other: "Vente à la dose modifiée pour {count} liquides" },
      },
      shop: {
        updated: "Informations de la boutique modifiées",
        link_changed: "Lien de la boutique modifié",
      },
      settings: {
        changed: "Réglage « {key} » : {from} → {to}",
      },
      cash: {
        opened: "A ouvert le tiroir, compté {counted}",
        closed: "A fermé le tiroir, compté {counted}",
        cash_in: "A mis {amount} de monnaie dans le tiroir",
        withdrawal: "A retiré {amount} du tiroir",
        refund: "Remboursement de {amount} payé depuis le tiroir",
        tab_payment: "{amount} remboursés sur un crédit, dans le tiroir",
        supplier_payout: "A payé {amount} à un fournisseur depuis le tiroir",
      },
      savings: {
        deposit: "A ajouté {amount} à l'épargne",
        withdraw: "A sorti {amount} de l'épargne",
      },
      supplier: {
        created: "A ajouté le fournisseur {supplier_name}",
        payment: "A payé {amount} à {supplier_name} ({source})",
      },
      delivery: {
        recorded: "Livraison de {supplier_name} — {total}",
        priced: "A chiffré la livraison de {supplier_name} — {total}",
      },
      mixing: {
        batch_produced: "{bottles} × {bottle_size_ml} ml de {recipe_name} mélangés",
        batch_shelved: "{bottles} flacons de {recipe_name} mis en rayon",
        batch_discarded: "Un lot de {recipe_name} jeté",
        material_adjusted: "{material_name} : {delta} {unit}",
      },
    },
    lines: {
      drawerCount: "Aurait dû contenir {expected} · écart {difference}",
      drawerSplit: "{taken} retirés, {left} laissés pour le lendemain",
      awaitingPrices: "Des prix restent à saisir",
      onTab: "Sur le compte de {name}",
      customer: "Client : {name}",
      discount: "Remise : {amount}",
      approvedBelowCost: "Sous le prix d'achat — validé par {name}",
      belowCost: "Sous le prix d'achat (offre ou pack)",
      reason: "Motif : {reason}",
    },
    fields: {
      name: "Nom",
      price: "Prix",
      cost_price: "Prix d'achat",
      discount_percentage: "Remise %",
      stock_quantity: "Stock",
      category: "Catégorie",
      is_draft: "Brouillon",
      is_active: "Active",
      title: "Titre",
      discount_value: "Valeur",
      start_date: "Du",
      end_date: "Au",
      items: "Contenu",
      products: "Produits concernés",
      ranges: "Gammes concernées",
      role: "Rôle",
      permissions: "Droits",
      pin: "Code PIN",
      label: "Libellé",
      amount: "Montant",
      occurred_on: "Date",
      slug: "Lien",
      menu_show_prices: "Menu avec prix",
      preorders_enabled: "Accepte les précommandes",
      warehouse_enabled: "Utilise un entrepôt",
      opening_hours: "Horaires",
    },
    sources: { drawer: "depuis le tiroir", safe: "depuis l'épargne", outside: "hors tiroir" },
    values: { yes: "oui", no: "non", changed: "modifié" },
  },

  ar: {
    title: "السجل",
    subtitle: "كل ما حدث في المتجر — من قام به ومتى وعلى أي جهاز. لا يمكن تعديل أي شيء هنا.",
    loadFailed: "تعذّر تحميل السجل.",
    empty: "لا شيء مسجّل في هذه الفترة.",
    loadMore: "عرض الإدخالات الأقدم",
    person: "الشخص",
    everyone: "الجميع",
    type: "النوع",
    everything: "الكل",
    unknownPerson: "لا أحد مسجّل الدخول",
    categories: {
      sales: "المبيعات",
      stock: "المخزون",
      catalogue: "المنتجات والأسعار",
      money: "المال",
      preorders: "الطلبات المسبقة",
      till: "الصندوق",
      team: "الفريق والإعدادات",
    },
    actions: {
      sale: {
        created: "بيع رقم {receipt} — {total}، {payment}",
        refunded: "استرداد البيع رقم {receipt} — {total}",
      },
      stock: {
        adjusted: "{product}: المخزون {from} ← {to} ({delta})",
        moved_to_warehouse: "نقل {quantity} × {product} إلى المخزن",
        moved_to_shop: "نقل {quantity} × {product} إلى المتجر",
        warehouse_adjusted: "{product}: مخزون المخزن {from} ← {to} ({delta})",
      },
      product: {
        created: "إضافة {product}",
        updated: "تعديل {product}",
        archived: "أرشفة {product}",
        deleted: "حذف {product}",
        restored: "استعادة {product}",
        converted_to_range: "تحويل {product} إلى المجموعة «{range_name}» ({variants} أنواع)",
      },
      offer: {
        created: "إنشاء العرض «{offer_title}»",
        updated: "تعديل العرض «{offer_title}»",
        deleted: "حذف العرض «{offer_title}»",
      },
      pack: {
        created: "إنشاء الباقة «{pack_name}»",
        updated: "تعديل الباقة «{pack_name}»",
        deleted: "حذف الباقة «{pack_name}»",
      },
      credit: {
        payment: "{credit_tab} دفع {amount} من دينه",
      },
      expense: {
        created: "مصروف: {label} — {amount}",
        updated: "تعديل المصروف «{label}»",
        deleted: "حذف المصروف «{label}» ({amount})",
      },
      preorder: {
        status: "طلب {customer} المسبق: {from} ← {to}",
      },
      till: {
        unlocked: "فتح الصندوق",
        pin_failed: "أُدخل رمز لا يطابق أحداً",
        device_activated: "تفعيل الجهاز «{device_label}»",
        device_revoked: "تحرير الجهاز «{device_label}»",
      },
      staff: {
        added: "إضافة {name} إلى الفريق",
        updated: "تغيير صلاحيات {name}",
        removed: "إزالة {name} من الفريق",
        password_reset: "إعادة تعيين كلمة مرور {name}",
        pin_changed: "تغيير رمز PIN الخاص بالصندوق",
      },
      catalog: {
        copied: "إضافة {added} منتجات من الكتالوج ({drafts} بحاجة إلى سعر)",
        copied_from_shop: {
          zero: "لم يُنسخ أي منتج من {from_shop}",
          one: "نسخ منتج واحد من {from_shop}",
          two: "نسخ منتجين من {from_shop}",
          few: "نسخ {count} منتجات من {from_shop}",
          many: "نسخ {count} منتجًا من {from_shop}",
          other: "نسخ {count} منتج من {from_shop}",
        },
      },
      doses: {
        bottle_opened: "فتح قارورة {product} للبيع بالجرعة",
        bottle_finished: "انتهاء قارورة {product}: {doses_sold} جرعة",
        bottle_cancelled: "إرجاع قارورة {product} فُتحت خطأً إلى المخزون",
        setup_changed: {
          zero: "تعديل البيع بالجرعة",
          one: "تعديل البيع بالجرعة لسائل واحد",
          two: "تعديل البيع بالجرعة لسائلين",
          few: "تعديل البيع بالجرعة لـ{count} سوائل",
          many: "تعديل البيع بالجرعة لـ{count} سائلًا",
          other: "تعديل البيع بالجرعة لـ{count} سائل",
        },
      },
      shop: {
        updated: "تعديل بيانات المتجر",
        link_changed: "تغيير رابط المتجر",
      },
      settings: {
        changed: "الإعداد «{key}»: {from} ← {to}",
      },
      cash: {
        opened: "فتح الدرج، وعدّ {counted}",
        closed: "أغلق الدرج، وعدّ {counted}",
        cash_in: "وضع {amount} من الفكة في الدرج",
        withdrawal: "أخذ {amount} من الدرج",
        refund: "دفع استرجاعًا بقيمة {amount} من الدرج",
        tab_payment: "سُدّد {amount} من دين، إلى الدرج",
        supplier_payout: "دفع {amount} لمورّد من الدرج",
      },
      savings: {
        deposit: "أضاف {amount} إلى المدخرات",
        withdraw: "سحب {amount} من المدخرات",
      },
      supplier: {
        created: "أضاف المورّد {supplier_name}",
        payment: "دفع {amount} إلى {supplier_name} ({source})",
      },
      delivery: {
        recorded: "توريد من {supplier_name} — {total}",
        priced: "سعّر التوريد من {supplier_name} — {total}",
      },
      mixing: {
        batch_produced: "خلط {bottles} × {bottle_size_ml} مل من {recipe_name}",
        batch_shelved: "وضع {bottles} قارورة من {recipe_name} على الرف",
        batch_discarded: "التخلص من دفعة {recipe_name}",
        material_adjusted: "{material_name}: {delta} {unit}",
      },
    },
    lines: {
      drawerCount: "كان يجب أن يحتوي {expected} · الفرق {difference}",
      drawerSplit: "أُخذ {taken}، وبقي {left} لليوم التالي",
      awaitingPrices: "بعض الأسعار لم تُدخل بعد",
      onTab: "على حساب {name}",
      customer: "الزبون: {name}",
      discount: "التخفيض: {amount}",
      approvedBelowCost: "تحت سعر الشراء — بموافقة {name}",
      belowCost: "تحت سعر الشراء (عرض أو باقة)",
      reason: "السبب: {reason}",
    },
    fields: {
      name: "الاسم",
      price: "السعر",
      cost_price: "سعر الشراء",
      discount_percentage: "التخفيض %",
      stock_quantity: "المخزون",
      category: "الفئة",
      is_draft: "مسودة",
      is_active: "نشط",
      title: "العنوان",
      discount_value: "القيمة",
      start_date: "من",
      end_date: "إلى",
      items: "المحتوى",
      products: "المنتجات المشمولة",
      ranges: "المجموعات المشمولة",
      role: "الدور",
      permissions: "الصلاحيات",
      pin: "رمز الصندوق",
      label: "التسمية",
      amount: "المبلغ",
      occurred_on: "التاريخ",
      slug: "الرابط",
      menu_show_prices: "القائمة تعرض الأسعار",
      preorders_enabled: "يقبل الطلبات المسبقة",
      warehouse_enabled: "يستخدم مخزناً",
      opening_hours: "ساعات العمل",
    },
    sources: { drawer: "من الدرج", safe: "من المدخرات", outside: "من خارج الدرج" },
    values: { yes: "نعم", no: "لا", changed: "تغيّر" },
  },
};

export default journal;
